// @flow
import type { Effect } from 'redux-saga';
import type { HTTPStatus } from '~/core/types/network';

import Cookie from '<drivers>/cookie';
import { getMigrations, startMigration } from '~/core/api/migrations';
import { call, put, select, take, fork, cancel, takeLatest } from 'redux-saga/effects';
import {
    migrationStatusResponse,
    startMigrationResponse,
    migrationStatusRequest,
    stopMigrationPoll,
    progressInstallStage
} from './actions';
import { STAGE_ERROR, STAGE_COMPLETE, STAGE_STATUS } from './constants';

import { delay } from 'redux-saga';
import { noop } from 'lodash/fp';
import messages from './intl';
import { feedback, alerts } from '~/core/constants';
import { getHub } from '~/core/state/devices/selectors';
import { pollApiStop, pollApiStart } from '~/core/state/hub/actions';
import { requestAlert } from '~/core/state/alerts/actions';
import {
    FAILED,
    FINISHED,
    SOURCE_HUB_OFFLINE,
    DESTINATION_HUB_OFFLINE,
    DESTINATION_NOT_FOUND,
    MIGRATION_STATUS_REQUEST,
    MIGRATION_POLL_INTERVAL,
    START_MIGRATION_POLL,
    START_MIGRATION_REQUEST,
    STOP_MIGRATION_POLL
} from './constants';

import handleApi from '~/core/state/handle-api';
import handleSaga from '~/core/state/handle-saga';

import type { StartMigrationAction } from './types';

export class MigrationError extends Error {
    status: HTTPStatus = 200;
    code: string = '';
    constructor(status: HTTPStatus, code: string) {
        super();
        this.status = status;
        this.code = code;
    }
}

export function* startMigrationSaga({
    payload: { code: hubCode }
}: StartMigrationAction): Generator<noop | Effect, *, *> {
    const endpoint = yield call(Cookie.get, 'honeycomb');
    const token = yield call(Cookie.get, 'token');
    const hub = yield select(getHub);

    const [response, error] = yield call(handleApi(startMigration), endpoint, token, hub.id, hubCode);

    if (!error) {
        const { migrations: [migration] } = response;
        yield put(pollApiStop());
        yield put(startMigrationResponse({ status: migration }));
        yield routeMigrationStatus(migration);
        return;
    }

    const { code: statusCode, status: [{ code: errorCode } = {}] = [] } = error;

    yield put(startMigrationResponse(new MigrationError(statusCode, errorCode)));

    if (statusCode === 404 || errorCode === DESTINATION_NOT_FOUND) {
        yield put(requestAlert(messages.hubNotFound, alerts.types.ERROR));
    } else if ([SOURCE_HUB_OFFLINE, DESTINATION_HUB_OFFLINE].includes(errorCode)) {
        yield put(requestAlert(messages.hubOffline, alerts.types.ERROR));
    } else {
        yield put(progressInstallStage(STAGE_ERROR));
    }
}

function* watchStartMigration(): Generator<Effect, *, *> {
    yield takeLatest(START_MIGRATION_REQUEST, handleSaga(startMigrationSaga));
}

export function* getMigrationStatusSaga(): Generator<Effect | noop, *, *> {
    yield put(pollApiStop());

    const endpoint = yield call(Cookie.get, 'honeycomb');
    const token = yield call(Cookie.get, 'token');

    const [response, error] = yield call(handleApi(getMigrations), endpoint, token);

    if (error) {
        return yield put(requestAlert(feedback.generic.UNEXPECTED_ERROR, alerts.types.ERROR));
    }

    const { migrations: [migration = {}] = [] } = response;

    if (migration.status === FAILED) {
        yield put(migrationStatusResponse(new MigrationError(200, migration.failureReason)));
    } else {
        yield put(migrationStatusResponse({ status: migration }));
    }

    yield routeMigrationStatus(migration, false);
}

function* watchGetMigrationStatus(): Generator<Effect, *, *> {
    yield takeLatest(MIGRATION_STATUS_REQUEST, handleSaga(getMigrationStatusSaga));
}

function* routeMigrationStatus({ status }, loadStatusPage = true) {
    if (status === FINISHED) {
        yield put(stopMigrationPoll());
        yield put(pollApiStart());
        yield put(progressInstallStage(STAGE_COMPLETE));
    } else if (status === FAILED) {
        yield put(stopMigrationPoll());
        yield put(pollApiStart());
        yield put(progressInstallStage(STAGE_ERROR));
    } else if (loadStatusPage) {
        yield put(progressInstallStage(STAGE_STATUS));
    }
}

function* pollMigrationStatus(): Generator<Effect, *, *> {
    while (true) {
        yield put(migrationStatusRequest());
        yield call(delay, MIGRATION_POLL_INTERVAL);
    }
}

function* watchPollMigrationStatus(): Generator<Effect, *, *> {
    while (true) {
        yield take(START_MIGRATION_POLL);
        const deamon = yield fork(pollMigrationStatus);
        yield take(STOP_MIGRATION_POLL);
        yield cancel(deamon);
    }
}

export default [watchStartMigration, watchGetMigrationStatus, watchPollMigrationStatus];



// WEBPACK FOOTER //
// ./src/core/state/install-nano1-upgrade/sagas.js